import React, { useEffect, useState } from 'react';
import { eventBridgeService, EVENT_TYPES } from '@trip-planner-monorepo/shared';
import { Modal, Button, Typography, Box, Grid, Paper, IconButton, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel, Divider } from '@mui/material';
import { MapPin, X, CheckCircle, AlertCircle, AlertTriangle, RefreshCw, Plus, List } from 'react-feather';

/**
 * GenericSuggestionPopup Component
 * 
 * Displays a popup dialog for immediate suggestions generated by the SuggestionService.
 * This component subscribes to SHOW_IMMEDIATE_SUGGESTION_POPUP events and publishes
 * USER_RESPONDED_TO_SUGGESTION events based on user actions.
 * 
 * FIXED: Now supports suggestion queuing to handle multiple simultaneous suggestions
 * without interference (e.g., place + days suggestions from same accommodation).
 */
const GenericSuggestionPopup = () => {
  const [open, setOpen] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [suggestionQueue, setSuggestionQueue] = useState([]); // Queue for multiple suggestions
  const [subscription, setSubscription] = useState(null);
  const [selectedPlaceId, setSelectedPlaceId] = useState('');

  useEffect(() => {
    // Subscribe to immediate suggestion popup events
    const sub1 = eventBridgeService.subscribe(
      EVENT_TYPES.SHOW_IMMEDIATE_SUGGESTION_POPUP,
      handleSuggestionEvent
    );

    // Subscribe to cleanup completion events for user notification
    const sub2 = eventBridgeService.subscribe(
      EVENT_TYPES.ACCOMMODATION_DAYS_CLEANUP_COMPLETED,
      handleCleanupCompletedEvent
    );

    setSubscription(() => () => {
      if (sub1) sub1.unsubscribe();
      if (sub2) sub2.unsubscribe();
    });

    // Cleanup subscription on unmount
    return () => {
      if (sub1) sub1.unsubscribe();
      if (sub2) sub2.unsubscribe();
    };
  }, []);

  // Add keyboard event handling
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!open || !suggestion) return;

      // For informational notifications, handle keyboard shortcuts for Close button
      if (suggestion.suggestionType === 'PLACE_ORPHANED_NOTIFICATION' ||
        suggestion.suggestionType === 'MISSING_DAYS_NOTIFICATION' ||
        suggestion.suggestionType === 'ACCOMMODATION_DELETION_CLEANUP_NOTIFICATION') {
        if (event.key === 'Escape' || event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleClose();
        }
      }
      // For other suggestion types, handle normal keyboard shortcuts
      else {
        if (event.key === 'Escape') {
          event.preventDefault();
          handleReject();
        } else if (event.key === 'Enter') {
          event.preventDefault();
          handleAccept();
        }
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, suggestion]);

  /**
   * Process the suggestion queue to show the next suggestion
   * Uses functional state updates to avoid stale closure issues
   */
  const processNextSuggestion = () => {
    // Use functional update to get the current queue state (avoids stale closure)
    setSuggestionQueue(currentQueue => {
      console.log(`GenericSuggestionPopup: processNextSuggestion called - current queue length: ${currentQueue.length}, open: ${open}`);

      if (currentQueue.length === 0) {
        console.log('GenericSuggestionPopup: Queue is empty, nothing to process');
        if (open) {
          // Schedule state cleanup outside this updater
          setTimeout(() => {
            setOpen(false);
            setSuggestion(null);
          }, 0);
        }
        return currentQueue; // Return unchanged queue
      }

      // If popup is already open, don't process the next suggestion yet
      if (open) {
        console.log('GenericSuggestionPopup: Popup is already open, waiting for current suggestion to complete');
        return currentQueue; // Return unchanged queue
      }

      const nextSuggestion = currentQueue[0];
      const remainingQueue = currentQueue.slice(1);

      console.log(`GenericSuggestionPopup: Processing suggestion ${nextSuggestion.id}, ${remainingQueue.length} remaining in queue`);

      // Schedule showing the popup outside this updater to avoid nested state updates
      setTimeout(() => {
        setSuggestion(nextSuggestion);
        console.log(`GenericSuggestionPopup: About to show popup for suggestion ${nextSuggestion.id}`);
        setOpen(true);

        // For disambiguation, set default selection
        if (nextSuggestion.suggestionType === 'PLACE_DISAMBIGUATION_REQUIRED') {
          // If there's a newPlaceOption, default to creating new place
          // If no newPlaceOption (exact match exists), default to the exact match
          if (nextSuggestion.data.newPlaceOption) {
            setSelectedPlaceId('create_new');
          } else if (nextSuggestion.data.similarPlaces && nextSuggestion.data.similarPlaces.length > 0) {
            // Find the exact match among similar places
            const inputCityName = nextSuggestion.data.cityName || nextSuggestion.data.newCityName || '';
            const normalizedInput = inputCityName.trim().toLowerCase();

            const exactMatch = nextSuggestion.data.similarPlaces.find(place => {
              const placeName = (place.name || '').trim().toLowerCase();
              const placeFullName = (place.fullName || '').trim().toLowerCase();
              return placeName === normalizedInput || placeFullName === normalizedInput;
            });

            // Default to exact match if found, otherwise first place
            if (exactMatch) {
              setSelectedPlaceId(exactMatch.id);
              console.log(`GenericSuggestionPopup: Defaulting to exact match: ${exactMatch.name}`);
            } else {
              setSelectedPlaceId(nextSuggestion.data.similarPlaces[0].id);
              console.log(`GenericSuggestionPopup: No exact match found, defaulting to first place: ${nextSuggestion.data.similarPlaces[0].name}`);
            }
          }
        }
      }, 0);

      return remainingQueue; // Return the updated queue
    });
  };

  const handleSuggestionEvent = (suggestionData) => {
    console.log('GenericSuggestionPopup: Received immediate suggestion:', suggestionData);

    // Add to queue instead of replacing current suggestion
    setSuggestionQueue(currentQueue => {
      const newQueue = [...currentQueue, suggestionData];
      console.log(`GenericSuggestionPopup: Added suggestion to queue. Queue length: ${newQueue.length}`);

      if (open) {
        console.log(`GenericSuggestionPopup: Popup already open, suggestion queued. Total in queue: ${newQueue.length}`);
      }

      return newQueue;
    });
  };

  const handleCleanupCompletedEvent = (cleanupData) => {
    console.log('GenericSuggestionPopup: Received cleanup completion event:', cleanupData);

    // Create a notification suggestion for the cleanup completion
    const notificationSuggestion = {
      id: `cleanup_notification_${Date.now()}`,
      suggestionType: 'ACCOMMODATION_DELETION_CLEANUP_NOTIFICATION',
      uiDetails: {
        title: 'Day entries cleaned up',
        message: cleanupData.totalCleaned > 0
          ? `Removed "${cleanupData.accommodationName}" from ${cleanupData.totalCleaned} day ${cleanupData.totalCleaned === 1 ? 'entry' : 'entries'}.`
          : `"${cleanupData.accommodationName}" was deleted. No day entries needed cleanup.`
      },
      data: cleanupData,
      notificationStrategy: 'immediate_prompt'
    };

    // Add the notification to the queue
    handleSuggestionEvent(notificationSuggestion);
  };

  // Process queue when it changes
  useEffect(() => {
    if (!open && suggestionQueue.length > 0) {
      console.log(`GenericSuggestionPopup: Queue effect triggered - open: ${open}, queue length: ${suggestionQueue.length}`);
      // Use a small timeout to prevent race conditions with rapid state updates
      const timeoutId = setTimeout(() => {
        processNextSuggestion();
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [suggestionQueue, open]);

  const handleAccept = () => {
    if (suggestion) {
      console.log('GenericSuggestionPopup: User accepted suggestion:', suggestion.id);

      let responseData = {
        suggestionId: suggestion.id,
        choice: 'accept'
      };

      // For disambiguation, include the user's choice
      if (suggestion.suggestionType === 'PLACE_DISAMBIGUATION_REQUIRED') {
        responseData.selectedPlaceId = selectedPlaceId;
      }

      eventBridgeService.publish(EVENT_TYPES.USER_RESPONDED_TO_SUGGESTION, responseData);
    }
    handleClose();
  };

  const handleReject = () => {
    if (suggestion) {
      console.log('GenericSuggestionPopup: User rejected suggestion:', suggestion.id);
      eventBridgeService.publish(EVENT_TYPES.USER_RESPONDED_TO_SUGGESTION, {
        suggestionId: suggestion.id,
        choice: 'reject'
      });
    }
    handleClose();
  };

  const handleClose = () => {
    console.log(`GenericSuggestionPopup: Closing popup. Current queue length: ${suggestionQueue.length}`);
    setOpen(false);
    setSuggestion(null);
    setSelectedPlaceId('');

    // The useEffect will handle processing the next suggestion when open becomes false
  };

  const getIcon = () => {
    if (!suggestion) return <AlertCircle size={24} color="#ff9800" />;

    switch (suggestion.suggestionType) {
      case 'PLACE_LINK_OR_CREATE_FROM_ACCOMMODATION':
        return suggestion.data?.needsCreation ?
          <Plus size={24} color="#4caf50" /> :
          <MapPin size={24} color="#2196f3" />;
      case 'PLACE_DISAMBIGUATION_REQUIRED':
        return <List size={24} color="#ff9800" />;
      case 'DAY_ENTRIES_FOR_ACCOMMODATION':
        return <CheckCircle size={24} color="#4caf50" />;
      case 'ACCOMMODATION_DATE_CHANGE_UPDATE':
        return <AlertCircle size={24} color="#2196f3" />;

      case 'ACCOMMODATION_DATE_MISMATCH_NOTIFICATION':
        return <AlertTriangle size={24} color="#ff9800" />;
      case 'MISSING_DAYS_NOTIFICATION':
        return <AlertCircle size={24} color="#ff9800" />;
      case 'ACCOMMODATION_DELETION_CLEANUP_NOTIFICATION':
        return <CheckCircle size={24} color="#4caf50" />;
      default:
        return <CheckCircle size={24} color="#4caf50" />;
    }
  };

  const getTitle = () => {
    if (!suggestion) return 'Suggestion';

    // Use custom title from rule if available
    if (suggestion.uiDetails?.title) {
      return suggestion.uiDetails.title;
    }

    switch (suggestion.suggestionType) {
      case 'PLACE_LINK_OR_CREATE_FROM_ACCOMMODATION':
        return suggestion.data?.needsCreation ?
          'Create New Place' :
          'Link to Existing Place';
      case 'PLACE_LINK_OR_CREATE_FROM_CITY_CHANGE':
        return suggestion.data?.needsCreation ?
          'Create New Place' :
          'Link to Existing Place';
      case 'PLACE_DISAMBIGUATION_REQUIRED':
        return 'Multiple Places Found';
      case 'PLACE_ORPHANED_NOTIFICATION':
        return 'Review Old Place';
      case 'DAY_ENTRIES_FOR_ACCOMMODATION':
        return 'Add to Daily Itinerary';
      case 'ACCOMMODATION_DATE_CHANGE_UPDATE':
        return 'Update Daily Itinerary';

      case 'ACCOMMODATION_DATE_MISMATCH_NOTIFICATION':
        return 'Date Mismatch';
      case 'MISSING_DAYS_NOTIFICATION':
        return 'No days for this accommodation';
      case 'ACCOMMODATION_DELETION_CLEANUP_NOTIFICATION':
        return 'Day entries cleaned up';
      default:
        return 'Suggestion';
    }
  };

  const getMessage = () => {
    if (!suggestion) return '';

    // Use custom message from rule if available
    if (suggestion.uiDetails?.message) {
      return suggestion.uiDetails.message;
    }

    switch (suggestion.suggestionType) {
      case 'PLACE_LINK_OR_CREATE_FROM_ACCOMMODATION':
        return suggestion.data?.needsCreation ?
          `Create a new place "${suggestion.data.cityName}" for accommodation "${suggestion.data.accommodationName}"?` :
          `Link accommodation "${suggestion.data.accommodationName}" to existing place "${suggestion.data.existingPlaceName}"?`;
      case 'PLACE_LINK_OR_CREATE_FROM_CITY_CHANGE':
        const oldCity = suggestion.data?.oldCityName || 'previous city';
        const newCity = suggestion.data?.newCityName || suggestion.data?.cityName;
        return suggestion.data?.needsCreation ?
          `You changed the city for "${suggestion.data.accommodationName}" from "${oldCity}" to "${newCity}". Would you like to create a new place for ${newCity}?` :
          `You changed the city for "${suggestion.data.accommodationName}" from "${oldCity}" to "${newCity}". Would you like to link to existing place "${suggestion.data.existingPlaceName}"?`;
      case 'PLACE_DISAMBIGUATION_REQUIRED':
        return `We found several places that might match "${suggestion.data.cityName}" for accommodation "${suggestion.data.accommodationName}". Please choose:`;
      case 'DAY_ENTRIES_FOR_ACCOMMODATION':
        return `Would you like to add "${suggestion.data.accommodationName}" to your daily itinerary?`;
      case 'ACCOMMODATION_DATE_CHANGE_UPDATE':
        return suggestion.uiDetails?.message || `The dates for "${suggestion.data.accommodationName}" have changed. Would you like to update your day entries?`;

      case 'ACCOMMODATION_DATE_MISMATCH_NOTIFICATION':
        return suggestion.uiDetails?.message || 'The accommodation dates don\'t match your existing day entries.';
      case 'MISSING_DAYS_NOTIFICATION':
        const accommodationName = suggestion.data?.accommodationName || 'accommodation';
        const startDate = suggestion.data?.dateRange?.startDate;
        const endDate = suggestion.data?.dateRange?.endDate;

        if (startDate && endDate && startDate !== endDate) {
          return `Your accommodation "${accommodationName}" has dates ${startDate} to ${endDate}, but these dates don't exist in your list of Days. You may wish to fix this by changing the accommodation dates or adding the missing dates to your Days list.`;
        } else if (startDate) {
          return `Your accommodation "${accommodationName}" has date ${startDate}, but this date doesn't exist in your list of Days. You may wish to fix this by changing the accommodation date or adding this date to your Days list.`;
        } else {
          return `Your accommodation "${accommodationName}" has dates that don't exist in your list of Days. You may wish to fix this by changing the accommodation dates or adding the missing dates to your Days list.`;
        }
      case 'PLACE_ORPHANED_NOTIFICATION':
        return suggestion.data?.oldCityName ?
          `The place "${suggestion.data.oldCityName}" is no longer referenced by any accommodation. You can remove it from your trip if you no longer need it.` :
          'This place is no longer referenced by any accommodation. You can remove it from your trip if you no longer need it.';
      case 'ACCOMMODATION_DELETION_CLEANUP_NOTIFICATION':
        return suggestion.uiDetails?.message || 'Day entries have been cleaned up.';
      default:
        return 'Would you like to proceed with this suggestion?';
    }
  };

  const renderDisambiguationOptions = () => {
    if (!suggestion || suggestion.suggestionType !== 'PLACE_DISAMBIGUATION_REQUIRED') {
      return null;
    }

    const { similarPlaces, newPlaceOption } = suggestion.data;

    return (
      <Box sx={{ mt: 2 }}>
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
            Choose an option:
          </FormLabel>
          <RadioGroup
            value={selectedPlaceId}
            onChange={(e) => setSelectedPlaceId(e.target.value)}
          >
            {/* Option to create new place - only show if newPlaceOption exists */}
            {newPlaceOption && (
              <>
                <FormControlLabel
                  value="create_new"
                  control={<Radio color="primary" />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Plus size={16} color="#4caf50" />
                      <Typography variant="body2">
                        <strong>Create and link to new place:</strong> "{newPlaceOption.fullName}"
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 1 }}
                />

                <Divider sx={{ my: 1 }} />
              </>
            )}

            {/* Options for existing places */}
            {similarPlaces.map((place) => (
              <FormControlLabel
                key={place.id}
                value={place.id}
                control={<Radio color="primary" />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MapPin size={16} color="#2196f3" />
                    <Box>
                      <Typography variant="body2">
                        <strong>Link to existing place:</strong> {place.name}
                        {/* Show context information inline after the name */}
                        {place.context && (
                          <span style={{ color: '#666', marginLeft: '4px' }}>
                            : {place.context}
                          </span>
                        )}
                        {/* Fallback to fullName if no context */}
                        {!place.context && place.fullName && place.fullName !== place.name && (
                          <span style={{ color: '#666', marginLeft: '4px' }}>
                            ({place.fullName})
                          </span>
                        )}
                      </Typography>
                    </Box>
                  </Box>
                }
                sx={{ mb: 1, alignItems: 'flex-start' }}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Box>
    );
  };

  if (!open || !suggestion) {
    return null;
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2
      }}
    >
      <Paper
        elevation={8}
        sx={{
          width: '100%',
          maxWidth: suggestion.suggestionType === 'PLACE_DISAMBIGUATION_REQUIRED' ? 600 : 500,
          maxHeight: '90vh',
          overflow: 'auto',
          p: 3,
          position: 'relative',
          borderRadius: 2
        }}
      >
        {/* Close button */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'text.secondary'
          }}
        >
          <X size={20} />
        </IconButton>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, pr: 4 }}>
          {getIcon()}
          <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
            {getTitle()}
          </Typography>
        </Box>

        {/* Message */}
        <Typography variant="body1" sx={{ mb: 3, color: 'text.primary' }}>
          {getMessage()}
        </Typography>

        {/* Disambiguation options */}
        {renderDisambiguationOptions()}

        {/* Action buttons */}
        {(suggestion.suggestionType === 'PLACE_ORPHANED_NOTIFICATION' ||
          suggestion.suggestionType === 'MISSING_DAYS_NOTIFICATION' ||
          suggestion.suggestionType === 'ACCOMMODATION_DATE_MISMATCH_NOTIFICATION' ||
          suggestion.suggestionType === 'ACCOMMODATION_DELETION_CLEANUP_NOTIFICATION') ? (
          // Show only Close button for informational notifications
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={handleClose}
              sx={{
                backgroundColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#1565c0'
                },
                minWidth: 120
              }}
            >
              Close
            </Button>
          </Box>
        ) : (
          // Show Accept/Ignore buttons for other suggestion types
          <Grid container spacing={2} sx={{ mt: 3 }}>
            <Grid size={6}>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleReject}
                sx={{
                  borderColor: 'grey.400',
                  color: 'text.secondary',
                  '&:hover': {
                    borderColor: 'grey.600',
                    backgroundColor: 'grey.50'
                  }
                }}
              >
                Ignore
              </Button>
            </Grid>
            <Grid size={6}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleAccept}
                disabled={suggestion.suggestionType === 'PLACE_DISAMBIGUATION_REQUIRED' && !selectedPlaceId}
                sx={{
                  backgroundColor: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#1565c0'
                  }
                }}
              >
                Accept
              </Button>
            </Grid>
          </Grid>
        )}
      </Paper>
    </Modal>
  );
};

export default GenericSuggestionPopup; 